/**
 * Stock Research — shared detail view (Investing Idea architecture
 * finalization, 2026-09-14).
 *
 * Per the finalized Investing Idea design: there is no new top-level "Stock
 * Research" nav page/route. Instead, clicking a stock row on any of Top
 * Opportunities (Market Opportunities Details List), Market Cap Segments,
 * NIFTY 50, Breakout, or Portfolio Tracker opens THIS shared dialog for
 * that symbol — one consistent detail view, reused everywhere, rather than
 * each page building its own.
 *
 * Reuses existing, already-shipped read-only APIs only:
 *  - `stockMarketService.getStockHistory` (6-month price/EMA/RSI/MACD +
 *    Breakout Score/status history + OHLC — the same call
 *    `StockDataPage.tsx`'s own detail dialog already makes)
 *  - `marketOpportunityService.getBreakoutDetail` (the canonical 7-weighted-
 *    indicator Breakout Score Engine V2 breakdown — the same call the
 *    Breakouts tab already makes on row-expand)
 *
 * No new backend endpoint, no score/indicator calculated in React — this
 * component only fetches and renders what `BreakoutCalculationService` /
 * `IndicatorEngineService` already computed and persisted. The 7-indicator
 * breakdown table itself is the shared `BreakoutBreakdown` component (see
 * `@/components/breakout/BreakoutBreakdown`), not a second implementation.
 *
 * A stock with fewer than `MIN_HISTORY_BARS` of price history has no
 * `MarketBreakoutResult` yet — `getBreakoutDetail` 404s in that case. This
 * is treated as an expected, informational state ("Not enough history yet
 * for a Breakout Score"), never a fabricated score and never a blocking
 * error for the rest of the dialog (the price/EMA/RSI/MACD charts above
 * still render normally from whatever history does exist).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import stockMarketService from "@/services/stockMarketService";
import marketOpportunityService from "@/services/marketOpportunityService";
import type { BreakoutDetailResponse, StockHistoryResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import {
  BreakoutBreakdown,
  BreakoutStatusBadge,
  BREAKOUT_STATUS_COLORS,
  BREAKOUT_STATUS_LABELS,
} from "@/components/breakout/BreakoutBreakdown";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

function num(value: string | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

const chartOptions = { responsive: true, plugins: { legend: { position: "top" as const } }, scales: { x: { display: false } } };

export function StockResearchDialog({ symbol, onClose }: { symbol: string; onClose: () => void }) {
  const [data, setData] = useState<StockHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Breakout Score Engine V2 breakdown — fetched independently of the price
  // history above (different endpoint, can fail/be absent independently;
  // see module docstring on the 404 -> "not enough history yet" handling).
  const [breakoutDetail, setBreakoutDetail] = useState<BreakoutDetailResponse | null>(null);
  const [breakoutLoading, setBreakoutLoading] = useState(true);
  const [breakoutError, setBreakoutError] = useState<string | null>(null);
  const [breakoutNotYetScored, setBreakoutNotYetScored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await stockMarketService.getStockHistory(symbol, 6);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;
    async function loadBreakout() {
      setBreakoutLoading(true);
      setBreakoutError(null);
      setBreakoutNotYetScored(false);
      try {
        const detail = await marketOpportunityService.getBreakoutDetail(symbol);
        if (!cancelled) setBreakoutDetail(detail);
      } catch (err) {
        if (cancelled) return;
        // A 404 here means this stock hasn't cleared the Breakout Score
        // Engine's own minimum-history requirement yet — an expected,
        // documented gap, not a failure. Any other error is shown normally.
        if (err instanceof AxiosError && err.response?.status === 404) {
          setBreakoutNotYetScored(true);
        } else {
          setBreakoutError(extractErrorMessage(err));
        }
      } finally {
        if (!cancelled) setBreakoutLoading(false);
      }
    }
    loadBreakout();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  const priceChart = useMemo(() => {
    if (!data) return null;
    const labels = data.prices.map((p) => p.trade_date);
    const emaByDate = new Map(data.indicators.map((i) => [i.trade_date, i]));
    return {
      labels,
      datasets: [
        {
          label: "Close",
          data: data.prices.map((p) => Number(p.close)),
          borderColor: "#0F3D63",
          backgroundColor: "#0F3D63",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "EMA 20",
          data: labels.map((d) => {
            const v = emaByDate.get(d)?.ema_20;
            return v ? Number(v) : null;
          }),
          borderColor: "#00B386",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "EMA 50",
          data: labels.map((d) => {
            const v = emaByDate.get(d)?.ema_50;
            return v ? Number(v) : null;
          }),
          borderColor: "#FB8C00",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "EMA 200",
          data: labels.map((d) => {
            const v = emaByDate.get(d)?.ema_200;
            return v ? Number(v) : null;
          }),
          borderColor: "#8E24AA",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  const rsiChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.indicators.map((i) => i.trade_date),
      datasets: [
        {
          label: "RSI (14)",
          data: data.indicators.map((i) => (i.rsi_14 ? Number(i.rsi_14) : null)),
          borderColor: "#1976D2",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  const macdChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.indicators.map((i) => i.trade_date),
      datasets: [
        {
          label: "MACD",
          data: data.indicators.map((i) => (i.macd ? Number(i.macd) : null)),
          borderColor: "#0F3D63",
          pointRadius: 0,
          tension: 0.1,
        },
        {
          label: "Signal",
          data: data.indicators.map((i) => (i.macd_signal ? Number(i.macd_signal) : null)),
          borderColor: "#C62828",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  const scoreChart = useMemo(() => {
    if (!data) return null;
    return {
      labels: data.scanner_history.map((s) => s.trade_date),
      datasets: [
        {
          label: "Breakout Score",
          data: data.scanner_history.map((s) => Number(s.breakout_score)),
          borderColor: "#2E7D32",
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    };
  }, [data]);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={0.5}>
          <span>{data ? `${data.symbol} — ${data.company_name}` : symbol}</span>
          {breakoutDetail && <BreakoutStatusBadge status={breakoutDetail.status} />}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <Stack spacing={3} mt={1}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Price (6mo) with EMA 20 / 50 / 200
              </Typography>
              {priceChart && <Line data={priceChart} options={chartOptions} />}
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    RSI (14)
                  </Typography>
                  {rsiChart && <Line data={rsiChart} options={chartOptions} />}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    MACD / Signal
                  </Typography>
                  {macdChart && <Line data={macdChart} options={chartOptions} />}
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Historical Breakout Score
              </Typography>
              {scoreChart && data.scanner_history.length > 0 ? (
                <Line data={scoreChart} options={chartOptions} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not enough warm-up history yet for a Breakout Score.
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Status History (most recent 15)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                {data.scanner_history
                  .slice(-15)
                  .reverse()
                  .map((s) => (
                    <Tooltip key={s.trade_date} title={`${s.trade_date}: Breakout Score ${s.breakout_score}`}>
                      <Chip
                        label={`${s.trade_date.slice(5)} · ${BREAKOUT_STATUS_LABELS[s.status]}`}
                        size="small"
                        sx={{ bgcolor: BREAKOUT_STATUS_COLORS[s.status], color: "#fff" }}
                      />
                    </Tooltip>
                  ))}
                {data.scanner_history.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No status history yet.
                  </Typography>
                )}
              </Stack>
            </Paper>

            {/* Breakout Score Engine V2 — the canonical 7-weighted-indicator
                breakdown, same shared component the Breakouts tab uses.
                Independent loading/error/absent state from everything
                above: a stock can have plenty of price/indicator history
                (charts above render fine) while still not having cleared
                the Breakout engine's own, separate minimum-history bar. */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Breakout Score Breakdown
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {breakoutNotYetScored ? (
                <Typography variant="body2" color="text.secondary">
                  Not enough history yet for a Breakout Score — this stock hasn't cleared the engine's minimum
                  warm-up period.
                </Typography>
              ) : (
                <BreakoutBreakdown detail={breakoutDetail ?? undefined} loading={breakoutLoading} error={breakoutError ?? undefined} />
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: "auto" }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                OHLC Data (most recent first)
              </Typography>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">High</TableCell>
                    <TableCell align="right">Low</TableCell>
                    <TableCell align="right">Close</TableCell>
                    <TableCell align="right">Volume</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...data.prices]
                    .reverse()
                    .map((p) => (
                      <TableRow key={p.trade_date}>
                        <TableCell>{p.trade_date}</TableCell>
                        <TableCell align="right">{num(p.open)}</TableCell>
                        <TableCell align="right">{num(p.high)}</TableCell>
                        <TableCell align="right">{num(p.low)}</TableCell>
                        <TableCell align="right">{num(p.close)}</TableCell>
                        <TableCell align="right">{p.volume.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default StockResearchDialog;
